/**
 * Redux witness watchers for media
 * @module services/media/watchers
 */
import { Map, Set, List } from 'immutable'
import { CONTENT_STORE_KEY } from 'services/content'
import { isLoggedIn } from '../auth'
import { SET_AUTH_DATA } from '../auth/redux/actions'
import { isPageReady } from '../pages'
import { SET_LOCATION_DATA, setLocation as setLocationAction } from '../location/redux/actions'
import { SET_PAGE_READY } from '../pages/redux/actions'
import { createViewRecords, findView } from '../data'
import { showErrorToast, showSuccessToast } from '../toast/redux/actions'
import { getFirstErrorMessage } from '../error'
import {
  GET_DATA_LIST,
  SET_DATA_LIST,
  PERSIST_DATA,
  getDataList as getDataListAction,
  setDataList as setDataListAction,
} from '../data/redux/actions'
import { MEDIA_ASSETS_STORE_KEY } from '../media-assets'
import { CONTENT_POLICY_ID_REGEX, CONTENT_POLICY_STORE_KEY } from '../content-policy'
import { getConfigDataServer } from '../config'
import {
  createMediaListViewOptions,
  createMediaDetailViewOptions,
  createMediaMediaAssetsViewOptions,
  isMediaDetailPage,
  getMediaList,
  getMediaInfo,
  MEDIA_STORE_KEY,
  MEDIA_INFO_STORE_KEY,
  isMedialListPage,
  upsertMedia,
  isMediaCreatePage,
  createMediaCreateViewOptions,
  deleteMedia,
} from '.'
import { DELETE_MEDIA } from './redux/actions'

/**
 * Returns true if the action has a storeKey of 'media'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isMediaDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === MEDIA_STORE_KEY
}

/**
 * Initialize media list page
 * @param {*} param0 The params
 */
export function initMediaListPage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = MEDIA_STORE_KEY
      const options = createMediaListViewOptions(state)
      const prevOptions = createMediaListViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const viewReady = view && options.equals(prevOptions)
      if (viewReady) {
        return
      }
      dispatch(
        getDataListAction({
          storeKey,
          options,
        }),
      )
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isMedialListPage(state)
  })
}

/**
 * Returns true if the action has a storeKey of 'media'
 * @param {Object} param0 The parameters
 * @param {Object} [param0.action={}] A redux action
 * @param {Object} [param0.action.payload] The action payload
 * @param {String} [param0.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals media
 */
function isGetDataListMediaAction ({ action = {} } = {}) {
  const { type, payload: { storeKey } = {} } = action
  return type === GET_DATA_LIST && storeKey === MEDIA_STORE_KEY
}

/**
 * Returns true if the action has a storeKey of 'mediaInfo'
 * @param {Object} param0 The parameters
 * @param {Object} [param0.action={}] A redux action
 * @param {Object} [param0.action.payload] The action payload
 * @param {String} [param0.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals media
 */
function isGetDataListMediaInfoAction ({ action = {} } = {}) {
  const { type, payload: { storeKey } = {} } = action
  return type === GET_DATA_LIST && storeKey === MEDIA_INFO_STORE_KEY
}

/**
 * Initialize media detail page
 * @param {*} param0 The params
 */
export function initMediaDetailPage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = MEDIA_STORE_KEY
      const options = createMediaDetailViewOptions(state)
      const mediaId = options.get('mediaId')
      const prevOptions = createMediaDetailViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const viewReady = view && options.equals(prevOptions)
      if (viewReady) {
        return
      }
      dispatch(
        getDataListAction({
          storeKey,
          options,
        }),
      )
      dispatch(
        getDataListAction({
          storeKey: CONTENT_STORE_KEY,
          options: Map({ mediaId }),
        }),
      )
      // Get all of the content policies for drop downs
      dispatch(
        getDataListAction({
          storeKey: CONTENT_POLICY_STORE_KEY,
          options: Map({ limit: 500 }),
        }),
      )
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isMediaDetailPage(state)
  })
}

/**
 * Initialize media create page
 * @param {*} param0 The params
 */
export function initMediaCreatePage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = MEDIA_STORE_KEY
      const options = createMediaCreateViewOptions(state)
      const prevOptions = createMediaCreateViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const mediaReady = view && options.equals(prevOptions)
      if (mediaReady) {
        return
      }
      // Get all of the content policies for drop downs
      dispatch(
        getDataListAction({
          storeKey: CONTENT_POLICY_STORE_KEY,
          options: Map({ limit: 500 }),
        }),
      )
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isMediaCreatePage(state)
  })
}

export function getDataListMedia ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const {
      payload: { storeKey = MEDIA_STORE_KEY, options },
    } = action
    try {
      if (!storeKey) {
        throw new Error('storeKey required to get media')
      }
      const mediaList = await getMediaList({
        auth,
        server: getConfigDataServer(config),
        offset: options.get('offset'),
        limit: options.get('limit'),
        mediaId:
          Set.isSet(options.get('mediaId')) || List.isList(options.get('mediaId'))
            ? options.get('mediaId').toJS()
            : options.get('mediaId'),
        contentId:
          Set.isSet(options.get('contentId')) || List.isList(options.get('contentId'))
            ? options.get('contentId').toJS()
            : options.get('contentId'),
        mediaMaestroTitleCode:
          Set.isSet(options.get('mediaMaestroTitleCode')) ||
          List.isList(options.get('mediaMaestroTitleCode'))
            ? options.get('mediaMaestroTitleCode').toJS()
            : options.get('mediaMaestroTitleCode'),
        use: options.get('use'),
      })
      const include = options.get('include') || Set()
      const media = mediaList.get('media') || List()
      // Get media info
      media.forEach((mediaItem) => {
        if (include.includes('media_info')) {
          dispatch(
            getDataListAction({
              storeKey: MEDIA_INFO_STORE_KEY,
              options: Map({ mediaId: mediaItem.get('mediaId') }),
            }),
          )
        }
      })
      if (include.includes('media_assets')) {
        // Get content media ids
        const mediaId = media
          .map((v) => v.get('mediaId'))
          .filter((v) => v)
          .toSet()
        if (!mediaId.isEmpty()) {
          dispatch(
            getDataListAction({
              storeKey: MEDIA_ASSETS_STORE_KEY,
              options: createMediaMediaAssetsViewOptions({ mediaId }),
            }),
          )
        }
      }
      if (include.includes('content_policy')) {
        // Get content media ids
        const policyId = media
          .map((v) => v.get('policyId'))
          .filter((v) => CONTENT_POLICY_ID_REGEX.test(v))
          .toSet()
        if (!policyId.isEmpty()) {
          dispatch(
            getDataListAction({
              storeKey: CONTENT_POLICY_STORE_KEY,
              options: Map({ policyId }),
            }),
          )
        }
      }
      // Add mediaInfo property to media
      dispatch(
        setDataListAction({
          storeKey,
          options,
          records: createViewRecords(media, 'mediaId'),
          total: mediaList.get('totalCount'),
          processing: false,
        }),
      )
    } catch (e) {
      dispatch(
        setDataListAction({
          storeKey: MEDIA_STORE_KEY,
          options,
          error: e,
          processing: false,
        }),
      )
    }
  }).when(isGetDataListMediaAction)
}

export function getDataListMediaInfo ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const {
      payload: { storeKey = MEDIA_INFO_STORE_KEY, options },
    } = action
    try {
      if (!storeKey) {
        throw new Error('storeKey required to get media info')
      }
      const mediaId = options.get('mediaId')
      let mediaInfo = await getMediaInfo({
        auth,
        server: getConfigDataServer(config),
        mediaId,
      })
      mediaInfo = mediaInfo.set('mediaId', mediaId)
      // Add mediaInfo property to media
      dispatch(
        setDataListAction({
          storeKey,
          options,
          records: createViewRecords(List([mediaInfo]), 'mediaId'),
          total: 1,
          processing: false,
        }),
      )
    } catch (e) {
      dispatch(
        setDataListAction({
          storeKey,
          options,
          error: e,
          processing: false,
        }),
      )
    }
  }).when(isGetDataListMediaInfoAction)
}

/**
 * Returns true if the action has a storeKey of 'mediaAsset'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isMediaAssetDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === MEDIA_ASSETS_STORE_KEY
}

/**
 * Set data list for a media asset
 */
export function setDataListMediaAsset ({ after }) {
  return after(SET_DATA_LIST, async ({ action, dispatch }) => {
    const { options } = action.payload
    // Check if we are setting the data list after a delete action
    const isDelete = options.get('delete')
    // Remove .first() usage: only refresh if you have a valid mediaId from options
    const mediaId = options.get('mediaId')
    if (isDelete && mediaId) {
      dispatch(
        getDataListAction({
          storeKey: MEDIA_ASSETS_STORE_KEY,
          options: createMediaMediaAssetsViewOptions({ mediaId }),
        }),
      )
    }
    // If no mediaId, do nothing (no error)
  }).when(isMediaAssetDataAction)
}

/**
 * Persist data for medaia create to backend
 */
export function persistDataMedia ({ after }) {
  return after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options, record } = action.payload
    try {
      const media = await upsertMedia({
        auth,
        server: getConfigDataServer(config),
        data: record,
      })
      dispatch(
        setDataListAction({
          storeKey,
          options,
          records: createViewRecords(List([media]), 'mediaId'),
          total: 1,
          processing: false,
        }),
      )
    } catch (e) {
      dispatch(
        setDataListAction({
          storeKey,
          options,
          processing: false,
          error: e,
        }),
      )
    }
  }).when(isMediaDataAction)
}

/**
 * Delete media
 */
export function deleteMediaWatcher ({ after }) {
  return after(DELETE_MEDIA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, mediaId } = action.payload

    dispatch(
      setDataListAction({
        storeKey,
        processing: true,
      }),
    )
    try {
      await deleteMedia({
        auth,
        server: getConfigDataServer(config),
        mediaId,
      })
      dispatch(
        setDataListAction({
          storeKey,
          processing: false,
        }),
      )
      dispatch(showSuccessToast('Media deleted successfully'))
      // Redirect to media list after successful deletion
      dispatch(
        setLocationAction({
          location: {
            href: '/media',
          },
        }),
      )
      window.history.pushState(null, null, '/media')
    } catch (e) {
      dispatch(
        setDataListAction({
          storeKey,
          processing: false,
          error: e,
        }),
      )
      dispatch(showErrorToast(getFirstErrorMessage(e, 'Failed to delete media. ')))
    }
  })
}
